/**
 * ProductHistoryTab V2
 *
 * Tab für globale Version Timeline des Produkts.
 * Zeigt alle Änderungen an Content Blocks, Assets und Channel Packages.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History, FileText, Radio, ChevronRight } from 'lucide-react';
import { useProductContentBlocks } from '@/hooks/useContentBlock';
import { useProductChannelPackages } from '@/hooks/useChannelPackage';
import { getBlockTypeInfo } from '@/hooks/useContentBlock';
import type { BlockType } from '@/types/content-blocks';
import { Button, Badge, Loading, EmptyState } from '@/components/v2/ui';
import { formatDateTime } from '@/lib/utils';

interface ProductHistoryTabProps {
  productId: string;
  /** Optional: Callback für globalen Offcanvas */
  onOpenVersionHistory?: (
    entityType: 'content_block' | 'channel_package' | 'product_asset_link',
    entityId: string,
    blockType?: BlockType,
    channelId?: string
  ) => void;
}

interface TimelineEntry {
  id: string;
  entityId: string;
  type: 'content_block' | 'product_asset_link' | 'channel_package';
  typeLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  entityName: string;
  versionNumber: number;
  changedBy: string;
  timestamp: string;
  diffSummary?: string;
  blockType?: BlockType;
  channelId?: string;
}

export function ProductHistoryTab({ productId, onOpenVersionHistory }: ProductHistoryTabProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | 'content' | 'assets' | 'channels'>('all');

  // Queries
  const contentBlocksQuery = useProductContentBlocks(productId);
  const channelPackagesQuery = useProductChannelPackages(productId);

  // Build timeline entries from content blocks
  const contentEntries: TimelineEntry[] =
    contentBlocksQuery.data?.content_blocks
      ?.filter((block) => block.current_version)
      ?.map((block) => {
        const version = block.current_version!;
        const typeInfo = getBlockTypeInfo(block.block_type);
        return {
          id: `content-${block.id}-${version.id}`,
          entityId: block.id,
          type: 'content_block' as const,
          typeLabel: typeInfo.label,
          icon: FileText,
          entityName: typeInfo.label,
          versionNumber: version.version_number,
          changedBy: version.changed_by_name || 'System',
          timestamp: version.created_at,
          diffSummary: version.diff_summary || undefined,
          blockType: block.block_type,
        };
      }) || [];

  // Build timeline entries from channel packages
  const channelEntries: TimelineEntry[] =
    channelPackagesQuery.data?.channel_packages
      ?.filter((pkg) => pkg.current_version)
      ?.map((pkg) => ({
        id: `channel-${pkg.id}-${pkg.current_version!.id}`,
        entityId: pkg.id,
        type: 'channel_package' as const,
        typeLabel: 'Channel',
        icon: Radio,
        entityName: pkg.channel?.name || 'Unbekannt',
        versionNumber: pkg.current_version!.version_number,
        changedBy: 'System',
        timestamp: pkg.current_version!.created_at,
        diffSummary: pkg.current_version!.diff_summary || undefined,
        channelId: pkg.channel_id,
      })) || [];

  // Combine and sort by timestamp (newest first)
  const allEntries = [...contentEntries, ...channelEntries].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // Apply filter
  const filteredEntries = allEntries.filter((entry) => {
    if (filter === 'all') return true;
    if (filter === 'content') return entry.type === 'content_block';
    if (filter === 'assets') return entry.type === 'product_asset_link';
    if (filter === 'channels') return entry.type === 'channel_package';
    return true;
  });

  // Loading State
  if (contentBlocksQuery.isLoading || channelPackagesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading size="lg" label={t('content.changeHistoryLoading')} />
      </div>
    );
  }

  return (
    <div className="product-history-tab space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h5 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
            {t('content.changeHistory')}
          </h5>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {t('content.changeHistoryDescription')}
          </p>
        </div>

        {/* Filter */}
        <div className="flex gap-1 rounded-lg border border-neutral-200 dark:border-neutral-800 p-1">
          <Button
            variant={filter === 'all' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            {t('common.all')}
          </Button>
          <Button
            variant={filter === 'content' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setFilter('content')}
          >
            Inhalte
          </Button>
          <Button
            variant={filter === 'assets' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setFilter('assets')}
          >
            {t('common.images')}
          </Button>
          <Button
            variant={filter === 'channels' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setFilter('channels')}
          >
            {t('content.channels')}
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {filteredEntries.length > 0 ? (
        <div className="space-y-3">
          {filteredEntries.map((entry) => {
            const Icon = entry.icon;
            const iconColor =
              entry.type === 'content_block'
                ? 'text-primary-600'
                : entry.type === 'product_asset_link'
                  ? 'text-green-600'
                  : 'text-primary-600';

            return (
              <button
                key={entry.id}
                className={`w-full text-left transition-all ${
                  onOpenVersionHistory
                    ? 'hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer'
                    : 'cursor-default'
                }`}
                onClick={() => {
                  if (onOpenVersionHistory) {
                    onOpenVersionHistory(entry.type, entry.entityId, entry.blockType, entry.channelId);
                  }
                }}
                disabled={!onOpenVersionHistory}
              >
                <div className="flex gap-3 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900">
                  {/* Indicator */}
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      entry.type === 'content_block'
                        ? 'bg-primary-100 dark:bg-primary-900/30'
                        : entry.type === 'product_asset_link'
                          ? 'bg-green-100 dark:bg-green-900/30'
                          : 'bg-primary-100 dark:bg-primary-900/30'
                    }`}>
                      <Icon className={`h-5 w-5 ${iconColor}`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-grow min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-neutral-900 dark:text-neutral-50">
                            {entry.entityName}
                          </span>
                          <Badge variant="secondary">V{entry.versionNumber}</Badge>
                          {entry.diffSummary && (
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">
                              "{entry.diffSummary}"
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                          {entry.changedBy}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 flex-shrink-0">
                        <span>{formatDateTime(entry.timestamp)}</span>
                        {onOpenVersionHistory && <ChevronRight className="h-4 w-4" />}
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<History className="h-12 w-12" />}
          title={t('content.noChanges')}
          description="Es wurden noch keine Versionen erstellt."
        />
      )}
    </div>
  );
}
